import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { InfoCard, CopyTextButton } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

function CodeBlock({ text }: { text: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        my: 1,
        backgroundColor: 'rgba(0,0,0,0.04)',
        borderRadius: 1,
        p: 1,
      }}
    >
      <Typography
        component="pre"
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          flex: 1,
          m: 0,
        }}
      >
        {text}
      </Typography>
      <CopyTextButton text={text} />
    </Box>
  );
}

const DAY_NAMES = [
  '',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

function SpecsSection({
  details,
}: {
  details: Record<string, unknown> | undefined;
}) {
  if (!details) return null;

  const {
    availabilityType,
    tier,
    dataDiskSizeGb,
    dataDiskType,
    backupEnabled,
    pointInTimeRecoveryEnabled,
    iamAuthentication,
  } = details as {
    availabilityType?: string;
    tier?: string;
    dataDiskSizeGb?: number;
    dataDiskType?: string;
    backupEnabled?: boolean;
    pointInTimeRecoveryEnabled?: boolean;
    iamAuthentication?: boolean;
  };

  const chips: React.ReactNode[] = [];

  if (availabilityType) {
    chips.push(
      <Chip
        key="availability"
        label={availabilityType}
        size="small"
        color={availabilityType === 'REGIONAL' ? 'success' : 'default'}
        variant="outlined"
      />,
    );
  }
  if (tier) {
    chips.push(
      <Chip key="tier" label={tier} size="small" variant="outlined" />,
    );
  }
  if (dataDiskSizeGb !== undefined && dataDiskType) {
    chips.push(
      <Chip
        key="disk"
        label={`${dataDiskSizeGb} GB ${dataDiskType}`}
        size="small"
        variant="outlined"
      />,
    );
  } else if (dataDiskSizeGb !== undefined) {
    chips.push(
      <Chip
        key="disk"
        label={`${dataDiskSizeGb} GB`}
        size="small"
        variant="outlined"
      />,
    );
  }
  if (backupEnabled !== undefined) {
    chips.push(
      <Chip
        key="backup"
        label={`Backups: ${backupEnabled ? 'on' : 'off'}`}
        size="small"
        color={backupEnabled ? 'success' : 'default'}
        variant="outlined"
      />,
    );
  }
  if (pointInTimeRecoveryEnabled !== undefined) {
    chips.push(
      <Chip
        key="pitr"
        label={`PITR: ${pointInTimeRecoveryEnabled ? 'on' : 'off'}`}
        size="small"
        variant="outlined"
      />,
    );
  }
  if (iamAuthentication !== undefined) {
    chips.push(
      <Chip
        key="iam-auth"
        label={`IAM auth: ${iamAuthentication ? 'on' : 'off'}`}
        size="small"
        color={iamAuthentication ? 'success' : 'default'}
        variant="outlined"
      />,
    );
  }

  if (chips.length === 0) return null;

  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Specs
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {chips}
      </Box>
    </>
  );
}

function NetworkSection({
  details,
}: {
  details: Record<string, unknown> | undefined;
}) {
  if (!details) return null;

  const { privateIp, publicIp, outgoingIp } = details as {
    privateIp?: string;
    publicIp?: string;
    outgoingIp?: string;
  };

  if (!privateIp && !publicIp && !outgoingIp) return null;

  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Network
      </Typography>
      {privateIp && (
        <>
          <Typography variant="caption" color="text.secondary">
            Private
          </Typography>
          <CodeBlock text={privateIp} />
        </>
      )}
      {publicIp && (
        <>
          <Typography variant="caption" color="text.secondary">
            Public
          </Typography>
          <CodeBlock text={publicIp} />
        </>
      )}
      {outgoingIp && (
        <>
          <Typography variant="caption" color="text.secondary">
            Outgoing
          </Typography>
          <CodeBlock text={outgoingIp} />
        </>
      )}
    </>
  );
}

function MaintenanceSection({
  details,
}: {
  details: Record<string, unknown> | undefined;
}) {
  if (!details) return null;

  const { maintenanceDay, maintenanceHour, scheduledMaintenanceStartTime } =
    details as {
      maintenanceDay?: number;
      maintenanceHour?: number;
      scheduledMaintenanceStartTime?: string;
    };

  if (
    maintenanceDay === undefined &&
    maintenanceHour === undefined &&
    !scheduledMaintenanceStartTime
  ) {
    return null;
  }

  const dayName =
    maintenanceDay !== undefined
      ? (DAY_NAMES[maintenanceDay] ?? `Day ${maintenanceDay}`)
      : undefined;
  const hourStr =
    maintenanceHour !== undefined ? `${maintenanceHour}:00 UTC` : undefined;

  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Maintenance
      </Typography>
      {(dayName || hourStr) && (
        <Typography variant="body2">
          Window: {[dayName, hourStr].filter(Boolean).join(' ')}
        </Typography>
      )}
      {scheduledMaintenanceStartTime && (
        <Box sx={{ mt: 0.5 }}>
          <Chip
            label={`Next: ${new Date(scheduledMaintenanceStartTime).toLocaleString()}`}
            size="small"
            color="warning"
            variant="outlined"
          />
        </Box>
      )}
    </>
  );
}

function EngineCommands({
  dbVersion,
  instanceConnectionName,
  iamAuthentication,
}: {
  dbVersion: string | undefined;
  instanceConnectionName: string;
  iamAuthentication: boolean | undefined;
}) {
  const useIamAuthn = iamAuthentication !== false;
  const proxyCmd = useIamAuthn
    ? `cloud-sql-proxy --auto-iam-authn ${instanceConnectionName}`
    : `cloud-sql-proxy ${instanceConnectionName}`;

  const mysqlBlock = (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Connect (MySQL)
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Username is the local-part of your @digistore24.team email.
      </Typography>
      <CodeBlock text="mysql -h 127.0.0.1 -P 3306 -u <YOUR_EMAIL_PREFIX>" />
    </>
  );

  const postgresBlock = (
    <>
      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        Connect (Postgres)
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Postgres uses the full email as user; pass <code>--auto-iam-authn</code>{' '}
        to the proxy.
      </Typography>
      <CodeBlock
        text={`psql "host=127.0.0.1 port=5432 dbname=<DBNAME> user=<YOU>@digistore24.team sslmode=disable"`}
      />
    </>
  );

  let engineBlock: React.ReactNode;
  if (!dbVersion) {
    engineBlock = (
      <>
        <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
          Engine unknown — both shown
        </Typography>
        {mysqlBlock}
        {postgresBlock}
      </>
    );
  } else if (dbVersion.startsWith('MYSQL_')) {
    engineBlock = mysqlBlock;
  } else if (dbVersion.startsWith('POSTGRES_')) {
    engineBlock = postgresBlock;
  } else {
    engineBlock = (
      <>
        <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
          Engine unknown — both shown
        </Typography>
        {mysqlBlock}
        {postgresBlock}
      </>
    );
  }

  return (
    <>
      <Typography variant="subtitle2">Start the proxy</Typography>
      <CodeBlock text={proxyCmd} />
      {iamAuthentication === false && (
        <Typography variant="caption" color="text.secondary">
          IAM auth is disabled on this instance; use a normal DB password
        </Typography>
      )}
      {engineBlock}
    </>
  );
}

export function CloudSqlConnectCard() {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  if (annotations['gcp.com/service'] !== 'cloudsql') {
    return null;
  }

  const namespace = entity.metadata.namespace ?? 'default';
  const name = entity.metadata.name;
  const location = annotations['gcp.com/location'] ?? '<region>';
  const instanceConnectionName = `${namespace}:${location}:${name}`;
  const dbVersion = annotations['gcp.com/database-version'];
  const state = annotations['gcp.com/state'];
  const details = (entity.spec?.gcpDetails ?? undefined) as
    Record<string, unknown> | undefined;
  const iamAuthentication = details?.iamAuthentication as boolean | undefined;

  return (
    <InfoCard title="How to Connect">
      <Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Instance connection name:
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {instanceConnectionName}
          </Typography>
          <CopyTextButton text={instanceConnectionName} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {dbVersion && (
            <Chip label={dbVersion} size="small" variant="outlined" />
          )}
          {state && <Chip label={state} size="small" variant="outlined" />}
        </Box>

        <SpecsSection details={details} />
        <NetworkSection details={details} />

        <EngineCommands
          dbVersion={dbVersion}
          instanceConnectionName={instanceConnectionName}
          iamAuthentication={iamAuthentication}
        />

        <MaintenanceSection details={details} />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
          <Link
            href="https://cloud.google.com/sql/docs/mysql/sql-proxy"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
          >
            Install cloud-sql-proxy
          </Link>
          <Link
            href="https://digistore.atlassian.net/wiki/spaces/IT/pages/5652873217"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
          >
            Cloud SQL Proxy guide (Confluence)
          </Link>
          <Link
            href="https://digistore.atlassian.net/wiki/spaces/IT/pages/5649498248"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
          >
            Digistore DB instance directory
          </Link>
        </Box>
      </Box>
    </InfoCard>
  );
}
